import { DialogOverlay } from '@reach/dialog';
import PropTypes from 'prop-types';
import * as React from 'react';
import warning from 'tiny-warning';
import { LegacyModalDialogContext } from './legacy.modal-dialog.context.js';
import { LegacyModalDialogVariantEnum } from './legacy.modal-dialog.types.js';
import { useTransition, TransitionStatus } from '../../../react/hooks/use-transition.js';
import { createComponent } from '../../../react/components/core/create-component.js';
import { SWAN_STYLE_KEY_MAP } from '../../../react/components/head/stylesheets.component.js';
import { isFunction } from '../../../core/utilities/is.utils.js';
import { ROOT_CLASSNAMES } from '../../../core/constants/globals.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const propTypes = {
    /**
   * Specify Modal type
   */ variant: PropTypes.oneOf([
        LegacyModalDialogVariantEnum.Menu,
        LegacyModalDialogVariantEnum.Standard,
        LegacyModalDialogVariantEnum.PanelRight,
        LegacyModalDialogVariantEnum.PanelTop,
        LegacyModalDialogVariantEnum.PanelLeft,
        LegacyModalDialogVariantEnum.PanelBottom
    ]),
    /**
   * Callback function which will be invoked when the modal wants to be dismissed.
   */ onRequestDismiss: PropTypes.func.isRequired,
    /**
   * Decide whether to fill the full screen.
   */ takeOver: PropTypes.bool,
    /**
   * Specify Body width
   */ bodyWidth: PropTypes.oneOf([
        'standard',
        'capped'
    ])
};
const LegacyModalDialog = createComponent({
    root: DialogOverlay,
    displayName: 'LegacyModalDialog',
    propTypes,
    requiredStyleKeys: SWAN_STYLE_KEY_MAP.legacyModalDialog,
    propConfig: {
        derivedProps (props) {
            const [titleId, setTitleId] = React.useState(undefined);
            const [footerPinned, setFooterPinned] = React.useState(undefined);
            const transitionState = useTransition(!!props.isOpen);
            return {
                titleId,
                footerPinned,
                transitionState,
                setTitleId,
                setFooterPinned
            };
        },
        processProps (props, derivedProps) {
            warning(props.component === undefined, 'ModalDialog does not support the `component` prop because it must render a third-party component');
            const { transitionState } = derivedProps;
            const { onDismiss, onRequestDismiss } = props;
            return _object_spread_props(_object_spread({}, props), {
                isOpen: transitionState !== TransitionStatus.EXITED,
                onDismiss (event) {
                    if (isFunction(onRequestDismiss)) {
                        onRequestDismiss();
                    }
                    if (isFunction(onDismiss)) {
                        onDismiss(event);
                    }
                }
            });
        },
        classNames (props) {
            const { takeOver } = props;
            const defaultClassNames = [
                ROOT_CLASSNAMES.base,
                'swan-legacy-modal-dialog-backdrop'
            ];
            if (takeOver) defaultClassNames.push('swan-legacy-modal-dialog-takeover-backdrop');
            return defaultClassNames;
        },
        wrapRoot (props, derivedProps, element) {
            const { transitionState, titleId, setTitleId, footerPinned, setFooterPinned } = derivedProps;
            const { variant = 'standard', onRequestDismiss, takeOver, bodyWidth } = props;
            return /*#__PURE__*/ React.createElement(LegacyModalDialogContext.Provider, {
                value: {
                    variant,
                    transitionState,
                    onRequestDismiss,
                    titleId,
                    setTitleId,
                    takeOver: !!takeOver,
                    bodyWidth: bodyWidth || 'standard',
                    footerPinned,
                    setFooterPinned
                }
            }, element);
        }
    }
});
LegacyModalDialog.defaultProps = {
    bodyWidth: 'standard',
    variant: 'standard'
};

export { LegacyModalDialog };
